import { DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Empty as AntEmpty, Radio, Tabs } from 'antd';
import { AntCol } from 'components/ant/AntCol';
import { AntRow } from 'components/ant/AntRow';
import { AntTooltip } from 'components/ant/AntTooltip/AntTooltip';
import { Button, If } from 'components/UI';
import PdfViewer from 'components/UI/PdfViewer';
import PreviewItem from 'components/UI/Preview/PreviewItem';
import ScsViewer from 'components/UI/ScsViewer';
import Spin from 'components/UI/Spin';
import { first, isEmpty, last, map } from 'es-toolkit/compat';
import { AttachmentsBucket, BucketItem, PreviewsTab } from 'interfaces';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect } from 'react';
import { routesApi } from 'routes/api';
import { useIsChildDeal } from 'shared/intercompany/useIsChildDeal';
import { useStore } from 'stores/RootStore';
import useDownload from 'utils/hooks/useDownload';

import ImgPreviewBig from './ImgPreviewBig';
import {
  BucketFilename,
  BucketName,
  Divider,
  Empty,
  PdfViewerWrapper,
  RowPreviewBig,
  ScsViewerWrapper,
} from './styled';

interface Props {
  url: string;
  skipFetch?: boolean;
  onSkipCleaningComplete?: () => void;
  ignoreCleaned?: boolean;
  disableSkipCleaning?: boolean;
}

const Content: FC<Props> = ({ url, skipFetch, onSkipCleaningComplete, ignoreCleaned, disableSkipCleaning }) => {
  const { previewsStore } = useStore();
  const downloader = useDownload();
  const { isChildDeal } = useIsChildDeal();

  useEffect(() => {
    if (!skipFetch) {
      void previewsStore.fetch(url);
    }
  }, [previewsStore, url, skipFetch]);

  useEffect(() => {
    if (previewsStore.openingPreviewId && previewsStore.fetchStatus.isFinished) {
      void previewsStore.loadPreview(previewsStore.openingPreviewId);
    }
  });

  const download = (filename: string) => {
    if (previewsStore.currentPreview) {
      void downloader(routesApi.attachedFileDownloadPath(String(previewsStore.currentPreview.id)), filename);
    }
  };

  const openOriginal = () => {
    if (previewsStore.currentPreview?.parentId) {
      void previewsStore.loadPreview(previewsStore.currentPreview.parentId);
    }
  };

  const openCleaned = () => {
    if (previewsStore.currentPreview?.cleanedId) {
      void previewsStore.loadPreview(previewsStore.currentPreview.cleanedId);
    }
  };

  const skipCleaning = () => {
    if (previewsStore.currentPreview && !previewsStore.currentPreview.cleanedId) {
      void previewsStore.skipCleaning(previewsStore.currentPreview.id, url).then(() => {
        onSkipCleaningComplete?.();
      });
    }
  };

  const onClick = (item: BucketItem) => {
    void previewsStore.loadPreview(item.cleanedId || item.id);
  };

  if (previewsStore.fetchStatus.isProcessing || !previewsStore.fetchStatus.isFinished) {
    return <Spin size="large" />;
  }

  const isSCS = !!String(previewsStore.currentPreview?.bigUrl).match(/.+\.(scs)[?]?/);

  const isPDF = isSCS ? false : last(previewsStore.currentPreview?.name?.split('.'))?.toLowerCase() === 'pdf';

  const isMessageSourceType = previewsStore.currentPreview?.sourceType === 'Message';

  const activeKey = (): PreviewsTab | undefined => {
    if (isMessageSourceType) {
      return PreviewsTab.Documents;
    }

    if (previewsStore.currentPreview) {
      return previewsStore.currentPreview.context === 'default' ? PreviewsTab.Drawings : PreviewsTab.Documents;
    }

    return first(Object.keys(previewsStore.schema)) as PreviewsTab | undefined;
  };

  const hasCleanedFile =
    previewsStore.currentPreview &&
    (previewsStore.currentPreview.cleanedId ||
      (previewsStore.currentPreview.parentId && !previewsStore.currentPreview.cleanedId));

  return (
    <AntRow style={{ height: '100%' }}>
      <AntCol sm={17} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {previewsStore.currentPreview && (
          <AntRow justify="space-between" style={{ alignItems: 'center' }}>
            {!ignoreCleaned && (
              <div>
                <Radio.Group style={{ marginBottom: '10px' }} value={previewsStore.isOriginal ? 'original' : 'cleaned'}>
                  <Radio.Button value="original" onClick={openOriginal}>
                    Original file
                  </Radio.Button>

                  {hasCleanedFile ? (
                    <Radio.Button value="cleaned" onClick={openCleaned}>
                      Cleaned
                    </Radio.Button>
                  ) : (
                    <Radio.Button value="cleaned" disabled>
                      <WarningOutlined style={{ marginRight: '5px' }} />
                      No Cleaned file
                    </Radio.Button>
                  )}
                </Radio.Group>

                {!hasCleanedFile && (
                  <AntTooltip title={disableSkipCleaning ? 'Skip cleaning is disabled for GSH DFM' : undefined}>
                    <Button
                      type="link"
                      onClick={skipCleaning}
                      disabled={previewsStore.isLoadingPreview || isChildDeal || disableSkipCleaning}
                      style={{ paddingRight: 0 }}
                    >
                      Skip cleaning
                    </Button>
                  </AntTooltip>
                )}
              </div>
            )}

            <Button
              type="link"
              onClick={() => download(previewsStore?.currentPreview?.name || '')}
              style={{ paddingRight: 0 }}
              icon={<DownloadOutlined />}
            >
              {previewsStore.currentPreview.name}
            </Button>
          </AntRow>
        )}

        <RowPreviewBig justify="center">
          {previewsStore.isLoadingPreview && <Spin size="large" />}

          <If condition={!previewsStore.isLoadingPreview}>
            {previewsStore.currentPreview ? (
              <>
                {isPDF && (
                  <PdfViewerWrapper>
                    <PdfViewer src={previewsStore.currentPreview.bigUrl} />
                  </PdfViewerWrapper>
                )}
                {isSCS && (
                  <ScsViewerWrapper>
                    <ScsViewer
                      src={previewsStore.currentPreview.bigUrl}
                      name={previewsStore.currentPreview.name}
                      dimensions={previewsStore.currentPreview.dimensions}
                    />
                  </ScsViewerWrapper>
                )}
                {!isPDF && !isSCS && (
                  <ImgPreviewBig
                    src={previewsStore.currentPreview.bigUrl}
                    ext={last(previewsStore.currentPreview.name.split('.'))}
                  />
                )}
              </>
            ) : (
              <Empty image={AntEmpty.PRESENTED_IMAGE_SIMPLE} description="No preview selected" />
            )}
          </If>
        </RowPreviewBig>
      </AntCol>

      <AntCol sm={1}>
        <Divider />
      </AntCol>

      <AntCol sm={6}>
        {!isEmpty(previewsStore.schema) && (
          <Tabs defaultActiveKey={activeKey()} size="small">
            {map(previewsStore.schema, (buckets: AttachmentsBucket[], tab: string) => (
              <Tabs.TabPane tab={tab} key={tab} disabled={isEmpty(buckets)}>
                {map(buckets, (bucket: AttachmentsBucket) => (
                  <AntRow key={`${Number(bucket.bucketId)}-${bucket.bucketName}`}>
                    <AntCol sm={24}>
                      <BucketName>
                        {bucket.bucketId && <span>{`ID-${bucket.bucketId} `}</span>}
                        {bucket.bucketName}
                      </BucketName>
                    </AntCol>

                    <AntCol sm={24} style={{ marginTop: '5px' }}>
                      <AntRow gutter={[16, 16]}>
                        {map(bucket.items, (item: BucketItem) => (
                          <AntCol sm={8} key={item.id}>
                            <AntRow
                              justify="center"
                              style={{
                                flexDirection: 'column',
                                textAlign: 'center',
                              }}
                            >
                              <PreviewItem preview={item} onClick={() => onClick(item)} ignoreCleaned={ignoreCleaned} />
                              <BucketFilename active={item.id === previewsStore.currentPreview?.id}>
                                {item.name}
                              </BucketFilename>
                            </AntRow>
                          </AntCol>
                        ))}
                      </AntRow>
                    </AntCol>
                  </AntRow>
                ))}
              </Tabs.TabPane>
            ))}
          </Tabs>
        )}
      </AntCol>
    </AntRow>
  );
};

export default observer(Content);
